/**
 * Tooltip creation, positioning, and management helpers.
 *
 * Unified tooltip system: text tooltips, HTML tooltips with rich content builders,
 * click-to-toggle tooltips for mobile, and composable hover behavior via TooltipHoverOptions.
 */

import {
  CSS_COLORS,
  DEVBAR_THEME,
  FONT_MONO,
  TAILWIND_BREAKPOINTS,
  withAlpha,
} from '../constants.js';
import type { DevBarState } from './types.js';

/** Base styles for tooltip containers */
const TOOLTIP_BASE_STYLES = {
  position: 'fixed',
  zIndex: '10004',
  backgroundColor: 'var(--devbar-color-bg-elevated)',
  border: `1px solid ${CSS_COLORS.border}`,
  borderRadius: '6px',
  padding: '10px 12px',
  fontSize: '0.6875rem',
  fontFamily: FONT_MONO,
  maxWidth: '280px',
  boxShadow: DEVBAR_THEME.shadows.dropSm,
  pointerEvents: 'auto',
} as const;

/** Create a tooltip container element and track it for cleanup */
export function createTooltipContainer(state: DevBarState): HTMLDivElement {
  const tooltip = document.createElement('div');
  tooltip.setAttribute('data-devbar', 'true');
  tooltip.setAttribute('data-devbar-tooltip', 'true');
  Object.assign(tooltip.style, TOOLTIP_BASE_STYLES);
  state.activeTooltips.add(tooltip);
  return tooltip;
}

/** Remove a tooltip and untrack it */
export function removeTooltip(state: DevBarState, tooltip: HTMLDivElement): void {
  tooltip.remove();
  state.activeTooltips.delete(tooltip);
}

/** Clear all active tooltips (called on re-render) */
export function clearAllTooltips(state: DevBarState): void {
  for (const tooltip of state.activeTooltips) {
    tooltip.remove();
  }
  state.activeTooltips.clear();
}

/** Add a bold title to tooltip (metric name, feature name, etc.) */
export function addTooltipTitle(
  state: DevBarState,
  container: HTMLElement,
  title: string,
  color?: string
): void {
  const titleEl = document.createElement('div');
  const titleColor = color || state.settingsManager.get('accentColor') || CSS_COLORS.primary;
  Object.assign(titleEl.style, {
    color: titleColor,
    fontWeight: '600',
    marginBottom: '4px',
  });
  titleEl.textContent = title;
  container.appendChild(titleEl);
}

/** Add a description paragraph to tooltip */
export function addTooltipDescription(container: HTMLElement, description: string): void {
  const descEl = document.createElement('div');
  Object.assign(descEl.style, {
    color: CSS_COLORS.text,
    marginBottom: '10px',
    lineHeight: '1.4',
  });
  descEl.textContent = description;
  container.appendChild(descEl);
}

/** Add a muted uppercase section header to tooltip */
export function addTooltipSectionHeader(container: HTMLElement, header: string): void {
  const headerEl = document.createElement('div');
  Object.assign(headerEl.style, {
    color: CSS_COLORS.textMuted,
    fontSize: '0.625rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px',
  });
  headerEl.textContent = header;
  container.appendChild(headerEl);
}

/** Add a colored row with dot + label + value (for thresholds) */
export function addTooltipColoredRow(
  container: HTMLElement,
  label: string,
  value: string,
  color: string,
  labelWidth = '70px'
): void {
  const row = document.createElement('div');
  Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '8px' });

  const dot = document.createElement('span');
  Object.assign(dot.style, {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: color,
    flexShrink: '0',
  });
  row.appendChild(dot);

  const labelSpan = document.createElement('span');
  Object.assign(labelSpan.style, {
    color,
    fontWeight: '500',
    minWidth: labelWidth,
  });
  labelSpan.textContent = label;
  row.appendChild(labelSpan);

  const valueSpan = document.createElement('span');
  Object.assign(valueSpan.style, { color: CSS_COLORS.textMuted });
  valueSpan.textContent = value;
  row.appendChild(valueSpan);

  container.appendChild(row);
}

/** Add an info row with label + value (for breakpoint details) */
export function addTooltipInfoRow(container: HTMLElement, label: string, value: string): void {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    gap: '8px',
    lineHeight: '1.4',
  });

  const labelSpan = document.createElement('span');
  Object.assign(labelSpan.style, { color: CSS_COLORS.textMuted });
  labelSpan.textContent = label;
  row.appendChild(labelSpan);

  const valueSpan = document.createElement('span');
  Object.assign(valueSpan.style, { color: CSS_COLORS.text });
  valueSpan.textContent = value;
  row.appendChild(valueSpan);

  container.appendChild(row);
}

/** Position tooltip above the anchor element, adjusting for screen edges */
export function positionTooltip(tooltip: HTMLElement, anchor: HTMLElement): void {
  const rect = anchor.getBoundingClientRect();
  const anchorCenterX = rect.left + rect.width / 2;
  const isRightSide = anchorCenterX > window.innerWidth / 2;

  tooltip.style.bottom = `${window.innerHeight - rect.top + 8}px`;

  document.body.appendChild(tooltip);

  const tooltipRect = tooltip.getBoundingClientRect();

  if (isRightSide) {
    // Right-align tooltip with anchor (tooltip extends leftward)
    const rightAlignedLeft = rect.right - tooltipRect.width;
    tooltip.style.left = `${Math.max(10, rightAlignedLeft)}px`;
  } else {
    // Left-align tooltip with anchor (tooltip extends rightward)
    tooltip.style.left = `${rect.left}px`;

    // Adjust if off-screen right
    const newTooltipRect = tooltip.getBoundingClientRect();
    if (newTooltipRect.right > window.innerWidth - 10) {
      tooltip.style.left = `${window.innerWidth - tooltipRect.width - 10}px`;
    }
  }

  // Final check: ensure not off-screen left
  const finalRect = tooltip.getBoundingClientRect();
  if (finalRect.left < 10) {
    tooltip.style.left = '10px';
  }
}

/** Options for composing additional hover behavior with tooltips */
type TooltipHoverOptions = {
  onEnter?: () => void;
  onLeave?: () => void;
};

function getTextTooltipColor(state: DevBarState, element: HTMLElement): string {
  return (
    element.style.color ||
    getComputedStyle(element).color ||
    state.settingsManager.get('accentColor') ||
    CSS_COLORS.primary
  );
}

/** Attach a plain-text tooltip to an element (evaluated at hover time) */
export function attachTextTooltip(
  state: DevBarState,
  element: HTMLElement,
  getText: () => string,
  hoverOptions?: TooltipHoverOptions
): void {
  attachHtmlTooltip(
    state,
    element,
    (tooltip) => {
      const text = getText();
      const lines = text.split('\n');
      const tooltipColor = getTextTooltipColor(state, element);
      for (const line of lines) {
        const div = document.createElement('div');
        Object.assign(div.style, {
          color: tooltipColor,
          lineHeight: '1.4',
        });
        div.textContent = line;
        tooltip.appendChild(div);
      }
    },
    hoverOptions
  );
}

/** Attach an HTML tooltip to an element with custom content builder */
export function attachHtmlTooltip(
  state: DevBarState,
  element: HTMLElement,
  buildContent: (tooltip: HTMLDivElement) => void,
  hoverOptions?: TooltipHoverOptions
): void {
  if (!state.options.showTooltips) return;

  let tooltipEl: HTMLDivElement | null = null;
  let hideTimeout: ReturnType<typeof setTimeout> | null = null;

  const cancelHide = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimeout = setTimeout(() => {
      if (tooltipEl) {
        removeTooltip(state, tooltipEl);
        tooltipEl = null;
      }
    }, 100);
  };

  element.onmouseenter = () => {
    // Suppress tooltips while a modal overlay is open
    if (state.overlayElement) return;
    cancelHide();
    hoverOptions?.onEnter?.();
    // Clear any existing tooltip for this element first
    if (tooltipEl) {
      removeTooltip(state, tooltipEl);
      tooltipEl = null;
    }
    tooltipEl = createTooltipContainer(state);
    // Keep tooltip open when mouse enters it
    tooltipEl.onmouseenter = cancelHide;
    tooltipEl.onmouseleave = scheduleHide;
    buildContent(tooltipEl);
    positionTooltip(tooltipEl, element);
  };

  element.onmouseleave = () => {
    scheduleHide();
    hoverOptions?.onLeave?.();
  };

  // Also clean up tooltip on click (in case element triggers re-render)
  const originalOnclick = element.onclick;
  element.onclick = (e) => {
    cancelHide();
    if (tooltipEl) {
      removeTooltip(state, tooltipEl);
      tooltipEl = null;
    }
    if (originalOnclick) {
      originalOnclick.call(element, e);
    }
  };
}

/**
 * Attach an HTML tooltip with click-to-toggle support for mobile.
 * - Click toggles tooltip open/closed (pinned state)
 * - Hover opens tooltip on mouseenter (if not pinned)
 * - Hover closes tooltip on mouseleave (if not pinned)
 */
export function attachClickToggleTooltip(
  state: DevBarState,
  element: HTMLElement,
  buildContent: (tooltip: HTMLDivElement) => void
): void {
  if (!state.options.showTooltips) return;

  let tooltipEl: HTMLDivElement | null = null;
  let isPinned = false;

  // Store original opacity to restore on deactivate
  const originalOpacity = element.style.opacity || '0.7';

  const setActiveState = (active: boolean) => {
    element.style.opacity = active ? '1' : originalOpacity;
  };

  const showTooltip = () => {
    if (tooltipEl) {
      removeTooltip(state, tooltipEl);
    }
    tooltipEl = createTooltipContainer(state);
    buildContent(tooltipEl);
    positionTooltip(tooltipEl, element);
  };

  const hideTooltip = () => {
    if (tooltipEl) {
      removeTooltip(state, tooltipEl);
      tooltipEl = null;
    }
  };

  // Click toggles pinned state
  element.onclick = (e) => {
    e.stopPropagation();
    // Suppress tooltips while a modal overlay is open
    if (state.overlayElement) return;
    if (isPinned) {
      // Unpin and hide
      isPinned = false;
      setActiveState(false);
      hideTooltip();
    } else {
      // Pin and show
      isPinned = true;
      setActiveState(true);
      showTooltip();
    }
  };

  // Hover shows tooltip (if not pinned)
  element.onmouseenter = () => {
    // Suppress tooltips while a modal overlay is open
    if (state.overlayElement) return;
    if (!isPinned) {
      setActiveState(true);
      showTooltip();
    }
  };

  // Hover hides tooltip (if not pinned)
  element.onmouseleave = () => {
    if (!isPinned) {
      setActiveState(false);
      hideTooltip();
    }
  };

  // Close pinned tooltip when clicking outside
  const handleDocumentClick = (e: MouseEvent) => {
    if (
      isPinned &&
      tooltipEl &&
      !element.contains(e.target as Node) &&
      !tooltipEl.contains(e.target as Node)
    ) {
      isPinned = false;
      setActiveState(false);
      hideTooltip();
    }
  };

  // Add document click listener
  document.addEventListener('click', handleDocumentClick);

  // Store cleanup function on element for later removal
  (element as HTMLElement & { _tooltipCleanup?: () => void })._tooltipCleanup = () => {
    document.removeEventListener('click', handleDocumentClick);
    hideTooltip();
  };
}

/** Add a keyboard shortcut row to tooltip */
export function addTooltipShortcut(container: HTMLElement, key: string, description: string): void {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    gap: '8px',
    alignItems: 'baseline',
  });

  const keySpan = document.createElement('span');
  Object.assign(keySpan.style, {
    color: CSS_COLORS.textMuted,
    fontSize: '0.625rem',
    minWidth: '60px',
    wordBreak: 'break-word',
  });
  keySpan.textContent = key;
  row.appendChild(keySpan);

  const descSpan = document.createElement('span');
  Object.assign(descSpan.style, {
    color: CSS_COLORS.text,
    wordBreak: 'break-word',
  });
  descSpan.textContent = description;
  row.appendChild(descSpan);

  container.appendChild(row);
}

// ---------------------------------------------------------------------------
// Status box helpers (warning, success, error, progress)
// ---------------------------------------------------------------------------

/** Configuration for a status box icon */
type StatusIconConfig = {
  text: string;
  color: string;
  extraStyles?: Partial<CSSStyleDeclaration>;
};

/**
 * Create a color-coded status container with an icon.
 * Shared foundation for warning, success, error, and progress boxes.
 */
function createStatusBox(
  color: string,
  iconConfig: StatusIconConfig,
  extraContainerStyles?: Partial<CSSStyleDeclaration>
): HTMLDivElement {
  const box = document.createElement('div');
  Object.assign(box.style, {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    padding: '6px 8px',
    backgroundColor: withAlpha(color, 8),
    border: `1px solid ${withAlpha(color, 19)}`,
    borderRadius: '4px',
    ...extraContainerStyles,
  });

  const icon = document.createElement('span');
  icon.textContent = iconConfig.text;
  Object.assign(icon.style, {
    color: iconConfig.color,
    flexShrink: '0',
    ...iconConfig.extraStyles,
  });
  box.appendChild(icon);

  return box;
}

/** Append a secondary text line below a primary line (used by success/error boxes) */
function appendSubtext(
  parent: HTMLElement,
  text: string,
  extraStyles?: Partial<CSSStyleDeclaration>
): void {
  const sub = document.createElement('div');
  Object.assign(sub.style, {
    color: CSS_COLORS.textMuted,
    fontSize: '0.625rem',
    marginTop: '2px',
    ...extraStyles,
  });
  sub.textContent = text;
  parent.appendChild(sub);
}

/** Add a warning message to tooltip */
export function addTooltipWarning(container: HTMLElement, text: string): void {
  const box = createStatusBox(
    CSS_COLORS.warning,
    { text: '\u26A0', color: CSS_COLORS.warning },
    { marginTop: '8px', fontSize: '0.625rem' }
  );

  const textSpan = document.createElement('span');
  Object.assign(textSpan.style, { color: CSS_COLORS.warning });
  textSpan.textContent = text;
  box.appendChild(textSpan);

  container.appendChild(box);
}

/** Add a success status message to tooltip */
export function addTooltipSuccess(container: HTMLElement, text: string, subtext?: string): void {
  const box = createStatusBox(CSS_COLORS.primary, {
    text: '\u2713',
    color: CSS_COLORS.primary,
    extraStyles: { fontWeight: '600' },
  });

  const textContainer = document.createElement('div');
  const mainText = document.createElement('div');
  Object.assign(mainText.style, { color: CSS_COLORS.primary, fontWeight: '500' });
  mainText.textContent = text;
  textContainer.appendChild(mainText);

  if (subtext) {
    appendSubtext(textContainer, subtext, { wordBreak: 'break-all' });
  }

  box.appendChild(textContainer);
  container.appendChild(box);
}

/** Add an error status message to tooltip */
export function addTooltipError(container: HTMLElement, title: string, message: string): void {
  const box = createStatusBox(CSS_COLORS.error, {
    text: '\u00D7',
    color: CSS_COLORS.error,
    extraStyles: { fontWeight: '600', fontSize: '0.875rem' },
  });

  const textContainer = document.createElement('div');
  const titleEl = document.createElement('div');
  Object.assign(titleEl.style, { color: CSS_COLORS.error, fontWeight: '500' });
  titleEl.textContent = title;
  textContainer.appendChild(titleEl);

  appendSubtext(textContainer, message);

  box.appendChild(textContainer);
  container.appendChild(box);
}

/** Add an "in progress" status to tooltip */
export function addTooltipProgress(container: HTMLElement, text: string): void {
  const box = createStatusBox(
    CSS_COLORS.info,
    {
      text: '~',
      color: CSS_COLORS.info,
      extraStyles: { fontWeight: '600', animation: 'pulse 1s infinite' },
    },
    { alignItems: 'center' }
  );

  const textSpan = document.createElement('span');
  Object.assign(textSpan.style, { color: CSS_COLORS.info });
  textSpan.textContent = text;
  box.appendChild(textSpan);

  container.appendChild(box);
}

// ---------------------------------------------------------------------------
// Composite tooltip builders
// ---------------------------------------------------------------------------

/** Shorthand helpers bound to a specific tooltip + state, passed to button content builders */
export type ButtonTooltipHelpers = {
  addTitle: (title: string) => void;
  addDescription: (desc: string) => void;
  addSectionHeader: (header: string) => void;
  addShortcut: (key: string, desc: string) => void;
  addWarning: (text: string) => void;
  addSuccess: (text: string, subtext?: string) => void;
  addError: (title: string, message: string) => void;
  addProgress: (text: string) => void;
};

/** Create bound helper functions scoped to a tooltip element */
function createBoundHelpers(
  state: DevBarState,
  tooltip: HTMLDivElement,
  titleColor: string
): ButtonTooltipHelpers {
  return {
    addTitle: (title: string) => addTooltipTitle(state, tooltip, title, titleColor),
    addDescription: (desc: string) => addTooltipDescription(tooltip, desc),
    addSectionHeader: (header: string) => addTooltipSectionHeader(tooltip, header),
    addShortcut: (key: string, desc: string) => addTooltipShortcut(tooltip, key, desc),
    addWarning: (text: string) => addTooltipWarning(tooltip, text),
    addSuccess: (text: string, subtext?: string) => addTooltipSuccess(tooltip, text, subtext),
    addError: (title: string, message: string) => addTooltipError(tooltip, title, message),
    addProgress: (text: string) => addTooltipProgress(tooltip, text),
  };
}

/** Attach a button tooltip with custom title color */
export function attachButtonTooltip(
  state: DevBarState,
  element: HTMLElement,
  titleColor: string,
  buildContent: (tooltip: HTMLDivElement, helpers: ButtonTooltipHelpers) => void
): void {
  attachHtmlTooltip(state, element, (tooltip) => {
    buildContent(tooltip, createBoundHelpers(state, tooltip, titleColor));
  });
}

/** Build a color-coded thresholds section (good / needs work / poor) */
function buildThresholdsSection(
  container: HTMLElement,
  thresholds: { good: string; needsWork: string; poor: string }
): void {
  addTooltipSectionHeader(container, 'Thresholds');

  const thresholdsContainer = document.createElement('div');
  Object.assign(thresholdsContainer.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  });

  addTooltipColoredRow(thresholdsContainer, 'Good', thresholds.good, CSS_COLORS.primary);
  addTooltipColoredRow(thresholdsContainer, 'Needs work', thresholds.needsWork, CSS_COLORS.warning);
  addTooltipColoredRow(thresholdsContainer, 'Poor', thresholds.poor, CSS_COLORS.error);

  container.appendChild(thresholdsContainer);
}

/** Attach a metric tooltip with title, description, and colored thresholds */
export function attachMetricTooltip(
  state: DevBarState,
  element: HTMLElement,
  title: string,
  description: string,
  thresholds: { good: string; needsWork: string; poor: string }
): void {
  attachHtmlTooltip(state, element, (tooltip) => {
    addTooltipTitle(state, tooltip, title);
    addTooltipDescription(tooltip, description);
    buildThresholdsSection(tooltip, thresholds);
  });
}

/**
 * Tooltip-shape view of Tailwind breakpoints. Single source of truth lives
 * in `../constants.js`; this projection just adapts the label format the
 * tooltip wants (e.g. "\u2265640px"). Adding a new breakpoint to constants
 * automatically picks up here without a second edit.
 */
const TAILWIND_BREAKPOINT_ROWS = (
  Object.entries(TAILWIND_BREAKPOINTS) as [string, { min: number }][]
).map(([name, info]) => ({
  name,
  range: info.min === 0 ? '<640px' : `\u2265${info.min}px`,
}));

/** Build a single breakpoint reference row, highlighting the active breakpoint */
function buildBreakpointRow(bpName: string, bpRange: string, isActive: boolean): HTMLDivElement {
  const row = document.createElement('div');
  Object.assign(row.style, { display: 'flex', gap: '8px' });

  const nameSpan = document.createElement('span');
  Object.assign(nameSpan.style, {
    color: isActive ? CSS_COLORS.primary : CSS_COLORS.textMuted,
    fontWeight: isActive ? '600' : '400',
    minWidth: '32px',
  });
  nameSpan.textContent = bpName;
  row.appendChild(nameSpan);

  const rangeSpan = document.createElement('span');
  Object.assign(rangeSpan.style, {
    color: isActive ? CSS_COLORS.text : CSS_COLORS.textMuted,
  });
  rangeSpan.textContent = bpRange;
  row.appendChild(rangeSpan);

  return row;
}

/** Build the breakpoint reference list showing all Tailwind breakpoints */
function buildBreakpointReferenceList(activeBreakpoint: string): HTMLDivElement {
  const bpContainer = document.createElement('div');
  Object.assign(bpContainer.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    fontSize: '0.625rem',
  });

  for (const bp of TAILWIND_BREAKPOINT_ROWS) {
    bpContainer.appendChild(buildBreakpointRow(bp.name, bp.range, bp.name === activeBreakpoint));
  }

  return bpContainer;
}

/** Attach a breakpoint tooltip showing current breakpoint and all breakpoint ranges */
export function attachBreakpointTooltip(
  state: DevBarState,
  element: HTMLElement,
  breakpoint: string,
  dimensions: string,
  breakpointLabel: string
): void {
  attachHtmlTooltip(state, element, (tooltip) => {
    addTooltipTitle(state, tooltip, 'Tailwind Breakpoint');

    // Current breakpoint info
    const currentSection = document.createElement('div');
    Object.assign(currentSection.style, { marginBottom: '10px' });
    addTooltipInfoRow(currentSection, 'Current:', `${breakpoint} (${breakpointLabel})`);
    addTooltipInfoRow(currentSection, 'Viewport:', dimensions);
    tooltip.appendChild(currentSection);

    // Breakpoints reference
    addTooltipSectionHeader(tooltip, 'Breakpoints');
    tooltip.appendChild(buildBreakpointReferenceList(breakpoint));
  });
}

/** Attach a simple info tooltip with title and description */
export function attachInfoTooltip(
  state: DevBarState,
  element: HTMLElement,
  title: string,
  description: string
): void {
  attachHtmlTooltip(state, element, (tooltip) => {
    addTooltipTitle(state, tooltip, title);

    const descEl = document.createElement('div');
    Object.assign(descEl.style, {
      color: CSS_COLORS.text,
      lineHeight: '1.4',
    });
    descEl.textContent = description;
    tooltip.appendChild(descEl);
  });
}
